import React from "react";
import { Avatar, Table, Tag, Button, Popover } from "antd";
import { EyeOutlined } from '@ant-design/icons';
import { getInitials } from "utility/helperFunctions";
import { tableHead } from "../data";
import { RiderDetailModal } from "./RiderDetailsModal";
import { useState } from "react";
import AdminBadge from "utility/AdminBadge";
import { UTCtoLocalDateTime } from "utility/dateTimeUtils";

export const RideListData = ({ riders, rideDetails }) => {

    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRider, setSelectedRider] = useState(null);

    const sortedRiders = React.useMemo(() => {
        if (!riders || !rideDetails?.createdBy) return riders || [];

        const creator = riders.find(rider => rider.userId === rideDetails.createdBy);
        const otherRiders = riders.filter(rider => rider.userId !== rideDetails.createdBy);

        return creator ? [creator, ...otherRiders] : otherRiders;
    }, [riders, rideDetails?.createdBy]);

    if (!riders || riders.length === 0) {
        return <p>No riders available for this ride.</p>;
    }

    const getPaymentDetail = (rider, field) => {
        if (rider.paymentDetails === null) {
            return "-";
        }


        if (!rider.paymentDetails || typeof rider.paymentDetails !== 'object') {
            return "N/A";
        }

        const paymentDetail = rider.paymentDetails;

        switch (field) {
            case 'transactionId':
                return paymentDetail.transactionId || "N/A";

            case 'date':
                try {
                    if (!paymentDetail.date) return "N/A";
                    return `${UTCtoLocalDateTime(paymentDetail.date).date}, ${UTCtoLocalDateTime(paymentDetail.date).time}`;
                } catch (error) {
                    console.error('Date parsing error:', error);
                    return "Invalid Date";
                }

            case 'paymentMode':
                const mode = paymentDetail.paymentMode;
                return (mode && mode !== "N/A") ? mode : "N/A";

            case 'amount':
                const amount = paymentDetail.amount;
                if (amount && amount !== 0) {
                    return `₹ ${parseFloat(amount).toFixed(2)}`;
                }
                return "N/A";

            case 'paymentStatus':
                return paymentDetail.paymentStatus || "N/A";

            default:
                return "N/A";
        }
    };


    const handleClickOpen = (rider) => {
        setSelectedRider(rider);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedRider(null);
    };

    const columnHeaderStyles = {
        color: "#656574",
        fontSize: "16px",
        fontWeight: "500",
        lineHeight: "100%",
        letterSpacing: "0%",
    }

    const columns = tableHead.map((column) => {
        switch (column.title) {
            case "Rider Name":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "profilePic",
                    key: "profilePic",
                    width: column.width,
                    render: (text, record) => (
                        <div style={{ display: "flex", justifyContent: 'left', alignItems: "center" }}>
                            <Avatar
                                size={40}
                                src={record?.profilePic}
                                style={{ backgroundColor: "#87d068", marginRight: 12, flexShrink: 0 }}
                            >
                                {!record.profilePic && getInitials(record.firstName, record.lastName)}
                            </Avatar>
                            <div>
                                <strong style={{ fontSize: "16px" }}>
                                    {record?.firstName} {record?.lastName}
                                </strong>
                                <br />
                                <span style={{ color: "#8c8c8c", fontSize: "14px" }}>@{record?.userName}</span>
                                <div style={{ display: 'flex' }}>
                                    {record?.userId === rideDetails?.createdBy ? <AdminBadge /> : ""}
                                </div>
                            </div>
                        </div>
                    ),
                };
            case "Contact No.":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "phoneNumber",
                    key: "phoneNumber",
                    width: column.width,
                    render: (text) => <span style={{ fontSize: "14px" }}>+91 {text || "N/A"}</span>,
                };

            case "Transaction Id":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "transactionId",
                    key: "transactionId",
                    width: column.width,
                    render: (text, record) => {
                        const transactionId = getPaymentDetail(record, 'transactionId');
                        const shouldTruncate = transactionId && transactionId !== "N/A" && transactionId !== "-" && transactionId.length > 22;
                        const truncatedId = shouldTruncate
                            ? `${transactionId.substring(0, 22)}...`
                            : transactionId;

                        const content = (
                            <span style={{ fontSize: "14px" }}>
                                {truncatedId}
                            </span>
                        );

                        return shouldTruncate ? (
                            <Popover
                                content={
                                    <div style={{ maxWidth: 300, wordBreak: 'break-all', fontSize: '12px' }}>
                                        {transactionId}
                                    </div>
                                }
                                trigger="hover"
                                placement="topLeft"
                            >
                                {content}
                            </Popover>
                        ) : content;
                    },
                };

            case "Date":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "transactionDate",
                    key: "transactionDate",
                    width: column.width,
                    render: (text, record) => (
                        <span style={{ fontSize: "14px" }}>
                            {getPaymentDetail(record, 'date')}
                        </span>
                    ),
                }

            case "Pay Mode":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "payMode",
                    key: "payMode",
                    width: column.width,
                    render: (text, record) => (
                        <span style={{ fontSize: "14px" }}>
                            {getPaymentDetail(record, 'paymentMode')}
                        </span>
                    ),
                };

            case "Amount Paid":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "amountPaid",
                    key: "amountPaid",
                    width: column.width,
                    render: (text, record) => (
                        <span style={{ fontSize: "14px" }}>
                            {getPaymentDetail(record, 'amount')}
                        </span>

                    ),
                };

            case "Ride Status":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "inRideMemberStatus",
                    key: "inRideMemberStatus",
                    width: column.width,
                    render: (status) => {
                        let displayStatus;
                        let statusCategory;

                        if (status === 'JOINED') {
                            displayStatus = "Ride in Progress";
                            statusCategory = "Ride in Progress";
                        }
                        else if (status === 'FINISHED') {
                            displayStatus = "Finished Ride";
                            statusCategory = "Finished Ride";
                        }
                        else if (
                            status === 'CANCELLED' ||
                            status === 'REMOVED' ||
                            status === 'DELETED' ||
                            status === 'NOT_COMPLETED'
                        ) {
                            displayStatus = "Cancelled Ride";
                            statusCategory = "Cancelled Ride";
                        }
                        else {
                            displayStatus = "Not Started";
                            statusCategory = "Not Started";
                        }

                        const styles = {
                            "Ride in Progress": {
                                color: "#11B95C",
                                backgroundColor: "#E7FAF0",
                            },
                            "Finished Ride": {
                                color: "#6528D8",
                                backgroundColor: "#F0E8FF",
                            },
                            "Cancelled Ride": {
                                color: "#FF4D4F",
                                backgroundColor: "#FFF1F0",
                            },
                            "Not Started": {
                                color: "#FFAA0F",
                                backgroundColor: "#FFF7EA",
                            }
                        };

                        return (
                            <Tag style={{
                                ...styles[statusCategory],
                                padding: "4px 12px",
                                fontSize: "14px",
                                border: "none",
                                borderRadius: "12px"
                            }}>
                                {displayStatus}
                            </Tag>
                        );
                    },
                };

            case "Action":
                return {
                    title: <span style={columnHeaderStyles}>{column.title}</span>,
                    dataIndex: "action",
                    key: "action",
                    width: column.width,
                    render: (text, record) =>
                    (
                        <div style={{ color: 'black' }}>
                            <Button
                                icon={<EyeOutlined style={{ fontSize: '20px' }} />}
                                onClick={() => handleClickOpen(record)}
                                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '44px', borderRadius: '8px', border: '1.5px solid #dddde1' }} >
                            </Button>
                        </div >
                    ),
                };

            default:
                return null;
        }
    });

    return (
        <div
            className="ride-riders-table"
            style={{
                display: "flex",
                flexDirection: "column",
            }}
        >

            <div
                style={{
                    backgroundColor: "#f5f6fa",
                    borderRadius: "8px",
                    overflow: "hidden",
                    width: "100%",
                }}
            >
                <Table
                    dataSource={sortedRiders.map((rider, index) => ({ ...rider, key: rider.rideMemberId || index }))}
                    columns={columns}
                    pagination={false}
                    bordered={false}
                    tableLayout="fixed"
                    rowClassName={(record, index) =>
                        index % 2 === 0 ? "table-row-light" : "table-row-dark"
                    }
                    style={{ border: "none", width: "100%" }}
                />
            </div>

            <RiderDetailModal
                open={modalVisible}
                onClose={handleModalClose}
                riderData={selectedRider}
            />

            <style>
                {`
          .ride-riders-table .ant-table-thead > tr > th {
            background-color: #f5f6fa; 
            font-weight: 600;
            text-align: left;
            padding: 16px;
            border-bottom: none; 
            font-size: 16px;
            color: #595959;
          }

          .ride-riders-table .table-row-light > td {
            background-color: white;
            padding: 16px;
            border-bottom: 1px solid #f0f0f0;
            vertical-align: middle;
          }

          .ride-riders-table .table-row-dark > td {
            background-color: #fafafa;
            padding: 16px;
            border-bottom: 1px solid #f0f0f0;
            vertical-align: middle;
          }

          .ride-riders-table .ant-table-tbody > tr:last-child > td {
            border-bottom: none;
          }

          .ride-riders-table .ant-table-tbody > tr:hover > td {
            background-color: #e6f7ff;
          }

          .ride-riders-table .ant-avatar {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            flex-shrink: 0;
          }

          .ride-riders-table .ant-avatar > img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .ride-riders-table .ant-tag {
            font-size: 12px;
            padding: 4px 8px;
          }
        `}
            </style>
        </div >
    );
};